import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import SignInPage from './pages/core-auth/SignInPage';
import CoreDashboardPage from './pages/core-dashboard/CoreDashboardPage';
import OnboardingPage from './pages/core-dashboard/OnboardingPage';
import MfaOnboardingPage from './pages/core-mfa/MfaOnboardingPage';
import SuperadminHomePage from './pages/core-management/SuperadminHomePage';
import TenantCurrentPage from './pages/core-management/TenantCurrentPage';
import SuperadminsPage from './pages/core-management/SuperadminsPage';
import FeatureCatalogPage from './pages/core-management/FeatureCatalogPage';
import RbacRolesPage from './pages/core-management/RbacRolesPage';
import FeatureRoleMatrixPage from './pages/core-management/FeatureRoleMatrixPage';
import RbacRolePermissionsPage from './pages/core-management/RbacRolePermissionsPage';
import UsersPage from './pages/core-management/UsersPage';
import CoreModulePage from './pages/core-base/CoreModulePage';
import ProfilePage from './pages/core-management/ProfilePage';
import MenuManagementPage from './pages/core-management/MenuManagementPage';
import FavoritesPage from './features/menu-core/frontend/FavoritesPage';
import EmpresasPage from './features/core-directorio/frontend/EmpresasPage';
import CargaMasivaEmpresasPage from './features/core-directorio/frontend/CargaMasivaEmpresasPage';
import CoreUbicacionesPage from './pages/core-ubicaciones/CoreUbicacionesPage';
import ProgramasPage from './features/aos-sercotec/frontend/ProgramasPage';
import ContactosPage from './pages/core-trabajadores/ContactosPage';
import CargaMasivaContactosPage from './pages/core-directorio/CargaMasivaContactosPage';
import CoreShellLayout from './components/CoreShellLayout';
import { isFrontendFeatureEnabled } from './platform/featureFlags';

const WowdashThemePage = lazy(() => import('./pages/theme-wowdash/WowdashThemePage'));

// Componente para proteger rutas que requieren autenticación
const ONBOARDING_PATHS = new Set(['/onboarding', '/onboarding/mfa']);

const ProtectedRoute = ({ children }) => {
  const token = sessionStorage.getItem('syrakis_token');
  const location = useLocation();
  const [onboardingRetry, setOnboardingRetry] = useState(0);
  const [onboardingState, setOnboardingState] = useState({ loading: Boolean(token), status: null, error: '' });

  useEffect(() => {
    if (!token) {
      setOnboardingState({ loading: false, status: null, error: '' });
      return undefined;
    }
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    setOnboardingState((current) => ({ ...current, loading: true, error: '' }));
    fetch('/api/onboarding', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      signal: controller.signal,
    }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || data.error || 'No se pudo validar el onboarding.');
      return { status: data.onboarding?.status === 'completed' ? 'completed' : 'pending' };
    }).then(({ status }) => {
      if (active) setOnboardingState({ loading: false, status, error: '' });
    }).catch((error) => {
      if (active) setOnboardingState({
        loading: false,
        status: null,
        error: controller.signal.aborted
          ? 'La validación del onboarding tardó demasiado. Reintenta para continuar.'
          : error.message,
      });
    });
    return () => { active = false; window.clearTimeout(timeout); controller.abort(); };
  }, [token, location.pathname, onboardingRetry]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }
  if (onboardingState.loading) {
    return <div className="dashboard-main-body"><div className="card p-24">Validando el estado inicial...</div></div>;
  }
  if (onboardingState.error) {
    return <div className="dashboard-main-body"><div className="alert alert-danger">{onboardingState.error}<div className="mt-16"><button type="button" className="btn btn-outline-danger" onClick={() => setOnboardingRetry((value) => value + 1)}>Reintentar validación</button></div></div></div>;
  }
  const onOnboardingPath = ONBOARDING_PATHS.has(location.pathname);
  if (onboardingState.status !== 'completed' && !onOnboardingPath) {
    return <Navigate to="/onboarding" replace />;
  }
  if (onboardingState.status === 'completed' && onOnboardingPath) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function OptionalWowdashReference() {
  if (!isFrontendFeatureEnabled('themeWowdashReference')) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Suspense fallback={<div className="dashboard-main-body"><div className="card p-24">Cargando biblioteca Wowdash...</div></div>}>
      <WowdashThemePage />
    </Suspense>
  );
}

export default function App() {
  return (
    <Router>
      <ToastContainer position="top-right" autoClose={3000} />
      <Routes>
        <Route path="/login" element={<SignInPage />} />
        <Route
              path="/dashboard"
              element={
                  <ProtectedRoute>
                      <CoreShellLayout>
                        <CoreDashboardPage />
                      </CoreShellLayout>
                  </ProtectedRoute>
              }
        />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/mfa"
          element={
            <ProtectedRoute>
              <MfaOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin"
          element={
            <ProtectedRoute>
              <CoreShellLayout>
                <SuperadminHomePage />
              </CoreShellLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/usuarios" element={<ProtectedRoute><CoreShellLayout><UsersPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/usuarios/nuevo" element={<ProtectedRoute><CoreShellLayout><UsersPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/usuarios/:id/editar" element={<ProtectedRoute><CoreShellLayout><UsersPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/core-roles" element={<ProtectedRoute><CoreShellLayout><RbacRolesPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/core-roles/:id/permissions" element={<ProtectedRoute><CoreShellLayout><RbacRolePermissionsPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/perfil" element={<ProtectedRoute><CoreShellLayout><ProfilePage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/menu-core" element={<ProtectedRoute><CoreShellLayout><MenuManagementPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/menu-core/favoritos" element={<ProtectedRoute><CoreShellLayout><FavoritesPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/directorio/empresas" element={<ProtectedRoute><CoreShellLayout><EmpresasPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/directorio/empresas/contactos" element={<ProtectedRoute><CoreShellLayout><ContactosPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/directorio/empresas/contactos/carga-masiva" element={<ProtectedRoute><CoreShellLayout><CargaMasivaContactosPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/directorio/empresas/carga-masiva" element={<ProtectedRoute><CoreShellLayout><CargaMasivaEmpresasPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/gestion-tenants" element={<ProtectedRoute><CoreShellLayout><TenantCurrentPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/superusuarios" element={<ProtectedRoute><CoreShellLayout><SuperadminsPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/features" element={<ProtectedRoute><CoreShellLayout><FeatureCatalogPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/features/:featureId/roles" element={<ProtectedRoute><CoreShellLayout><FeatureRoleMatrixPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/core-ubicaciones" element={<ProtectedRoute><CoreShellLayout><CoreUbicacionesPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/aos/programas" element={<ProtectedRoute><CoreShellLayout><ProgramasPage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/core-policies/mfa" element={<ProtectedRoute><CoreShellLayout><CoreModulePage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/soporte-tecnico" element={<ProtectedRoute><CoreShellLayout><CoreModulePage /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/theme-wowdash" element={<ProtectedRoute><CoreShellLayout><OptionalWowdashReference /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/theme-wowdash/:pageKey/:id" element={<ProtectedRoute><CoreShellLayout><OptionalWowdashReference /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/theme-wowdash/:pageKey" element={<ProtectedRoute><CoreShellLayout><OptionalWowdashReference /></CoreShellLayout></ProtectedRoute>} />
        <Route path="/theme-wowdash/*" element={<ProtectedRoute><CoreShellLayout><OptionalWowdashReference /></CoreShellLayout></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
}
